import {
  Alert,
  Anchor,
  Button,
  Card,
  Grid,
  Group,
  Loader,
  Pagination,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
  useComputedColorScheme,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconDownload, IconSearch } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import {
  api,
  ApiError,
  type AggregateResult,
  type ChartWidget,
  type LayoutBlock,
  type MapBlock,
  type ProjectDetail,
  type ProjectVersion,
  type StatTileWidget,
  type TablePage,
  type TableWidget,
  type TextTileWidget,
} from "@/api/client";
import { ChartCard } from "@/viz/ChartCard";
import { type GeoJsonCollection } from "@/viz/ClusterMap";
import { DataTable } from "@/viz/DataTable";
import { StatTile } from "@/viz/StatTile";
import { TextTile } from "@/viz/TextTile";

/**
 * A dashboard is whatever dashboard.yaml says it is (§7.1).
 *
 * Nothing here knows what a project contains: the layout arrives from the API
 * with each widget's id resolved, and each widget fetches its own data by that
 * id. Adding a chart to a dashboard is an edit to a YAML file, not a deploy.
 */
export function ProjectPage() {
  const { slug = "" } = useParams<{ slug: string }>();

  const project = useQuery<ProjectDetail>({
    queryKey: ["project", slug],
    queryFn: () => api.get<ProjectDetail>(`/api/projects/${slug}`),
  });

  useLiveRefresh(slug);

  if (project.isLoading) return <Loader />;

  if (project.error) {
    return (
      <Alert color="red" title="Could not load project">
        {project.error instanceof Error ? project.error.message : "Unexpected error."}
      </Alert>
    );
  }

  const detail = project.data!;
  // A plain heading is the default for every project that hasn't opted into
  // either — most of them. Only a project with something extra to show (a
  // partner, or a declared accent color) gets the card treatment, so nothing
  // changes visually for projects that never set these.
  const hasHeaderExtras = Boolean(detail.color || detail.partner_name);

  return (
    <Stack gap="lg">
      {hasHeaderExtras ? (
        <Card
          padding="lg"
          withBorder
          style={
            detail.color
              ? { backgroundColor: `var(--mantine-color-${detail.color}-light)` }
              : undefined
          }
        >
          <Title order={2}>{detail.title}</Title>
          {detail.description && (
            <Text c="dimmed" mt={4}>
              {detail.description}
            </Text>
          )}
          {detail.partner_name && (
            <Text size="sm" mt="sm">
              Partner:{" "}
              {detail.partner_url ? (
                <Anchor href={detail.partner_url} target="_blank" rel="noopener noreferrer">
                  {detail.partner_name}
                </Anchor>
              ) : (
                detail.partner_name
              )}
            </Text>
          )}
        </Card>
      ) : (
        <div>
          <Title order={2}>{detail.title}</Title>
          {detail.description && (
            <Text c="dimmed" mt={4}>
              {detail.description}
            </Text>
          )}
        </div>
      )}

      {/* A broken config degrades to a card on this project alone, never a
          site-wide failure. */}
      {detail.config_error && (
        <Alert
          color="orange"
          icon={<IconAlertTriangle size={18} />}
          title="This project's configuration has a problem"
        >
          {detail.config_error}
        </Alert>
      )}

      {detail.layout.length === 0 && !detail.config_error && (
        <Alert color="gray">
          This project declares no layout yet. Add a <code>layout:</code> block to
          its <code>dashboard.yaml</code> to build the dashboard.
        </Alert>
      )}

      {detail.layout.map((block, index) => (
        <Block key={index} block={block} slug={slug} />
      ))}
    </Stack>
  );
}

function Block({ block, slug }: { block: LayoutBlock; slug: string }) {
  switch (block.type) {
    case "stat_row":
      return (
        <Grid>
          {block.tiles.map((tile) => (
            <Grid.Col key={tile.id} span={{ base: 12, xs: 6, md: 3 }}>
              <Tile slug={slug} tile={tile} />
            </Grid.Col>
          ))}
        </Grid>
      );

    case "row":
      return (
        <Grid>
          {block.widgets.map((widget) => (
            <Grid.Col
              key={widget.id}
              span={{ base: 12, lg: Math.max(4, 12 / block.widgets.length) }}
            >
              <Chart slug={slug} widget={widget} />
            </Grid.Col>
          ))}
        </Grid>
      );

    case "table":
      return <TableBlock slug={slug} widget={block} />;

    case "markdown":
      // Plain text, not parsed markdown — real markdown rendering (bold,
      // links, lists) is a phase 4 item. This covers the common case today:
      // a short section heading between layout blocks, using `source`
      // verbatim as a label.
      return (
        <Title order={3} mt="xs">
          {block.source}
        </Title>
      );

    case "text_stat":
      return <TextTileBlock slug={slug} widget={block} />;

    case "map":
      return <MapBlockView slug={slug} block={block} />;
  }
}

/** §7.4 puts this between 30 and 60 seconds; the data refreshes hourly at best. */
const POLL_INTERVAL_MS = 45_000;

/**
 * Refetch this project's widgets when its files change on disk (§7.4).
 *
 * Polling rather than WebSockets: through IIS and ARR a socket needs the
 * WebSocket Protocol feature and extra proxy configuration, which is real
 * Windows complexity bought for latency nobody needs on data that refreshes
 * hourly or daily.
 *
 * The version endpoint only stats files, and the widget endpoints already
 * revalidate with ETags, so a poll that finds no change costs almost nothing
 * and a poll that does find one mostly returns 304s.
 */
function useLiveRefresh(slug: string) {
  const client = useQueryClient();
  const seen = useRef<string | null>(null);

  const { data } = useQuery<ProjectVersion>({
    queryKey: ["version", slug],
    queryFn: () => api.get<ProjectVersion>(`/api/projects/${slug}/version`),
    refetchInterval: POLL_INTERVAL_MS,
    // Left at the default, which pauses the interval for a backgrounded tab:
    // nobody is reading a dashboard they cannot see.
  });

  const version = data?.version;

  useEffect(() => {
    if (!version) return;

    // The first response establishes the baseline rather than counting as a
    // change; otherwise every mount would refetch everything it just fetched.
    if (seen.current === null) {
      seen.current = version;
      return;
    }
    if (seen.current === version) return;

    seen.current = version;
    // dashboard.yaml is one of the files covered, so the layout itself may have
    // changed — refetch it alongside the widget data. "geometry" is included
    // too: the version hash covers every file in the project, including a
    // map's GeoJSON, so a redrawn boundary must repaint. This looks wasteful
    // next to the geometry endpoint's whole reason for existing — a poll that
    // finds nothing changed should cost almost nothing — but it doesn't
    // undercut it: invalidating only marks the query stale and triggers a
    // revalidation request, and that request 304s exactly when the geometry
    // file didn't move, per its own ETag. The two-endpoint split was built for
    // that no-change case, not to dodge the poll's invalidation entirely.
    for (const key of ["project", "widget", "table", "geometry"]) {
      client.invalidateQueries({ queryKey: [key, slug] });
    }
  }, [version, slug, client]);
}

function useWidgetData(slug: string, widgetId: string) {
  return useQuery<AggregateResult>({
    queryKey: ["widget", slug, widgetId],
    queryFn: () =>
      api.get<AggregateResult>(`/api/projects/${slug}/widgets/${widgetId}/data`),
    // Hold the previous render rather than flashing a skeleton on refetch.
    placeholderData: (previous) => previous,
  });
}

function useWidgetGeometry(slug: string, widgetId: string) {
  return useQuery<GeoJsonCollection>({
    queryKey: ["geometry", slug, widgetId],
    queryFn: () =>
      api.get<GeoJsonCollection>(`/api/projects/${slug}/widgets/${widgetId}/geometry`),
    placeholderData: (previous) => previous,
  });
}

function Tile({ slug, tile }: { slug: string; tile: StatTileWidget }) {
  const query = useWidgetData(slug, tile.id);

  if (query.error) return <WidgetError title={tile.title} error={query.error} />;

  return (
    <StatTile
      label={tile.title}
      value={query.data?.value ?? null}
      precision={tile.precision}
      isRefreshing={query.isFetching}
      color={tile.color}
      icon={tile.icon}
      footer={tile.footer.map((item, index) => ({
        label: item.label,
        hasValue: item.has_value,
        value: query.data?.footer_values[index] ?? null,
      }))}
    />
  );
}

function TextTileBlock({ slug, widget }: { slug: string; widget: TextTileWidget }) {
  const query = useWidgetData(slug, widget.id);

  if (query.error) return <WidgetError title={widget.title} error={query.error} />;

  return (
    <TextTile
      label={widget.title}
      value={query.data?.text ?? null}
      isRefreshing={query.isFetching}
    />
  );
}

function Chart({ slug, widget }: { slug: string; widget: ChartWidget }) {
  const query = useWidgetData(slug, widget.id);

  if (query.error) return <WidgetError title={widget.title} error={query.error} />;

  const data = query.data;

  return (
    <ChartCard
      title={widget.title}
      form={widget.type}
      subtitle={`${(data?.total_rows ?? 0).toLocaleString()} rows`}
      keys={data?.keys ?? []}
      values={data?.values ?? []}
      keyLabel={widget.group_by}
      valueLabel={measureLabel(widget.agg, data?.column ?? null)}
      isRefreshing={query.isFetching}
      thresholds={widget.thresholds}
      footnote={
        data?.truncated
          ? `Showing the top ${data.keys.length} of ${data.total_groups} groups.`
          : undefined
      }
    />
  );
}

function MapBlockView({ slug, block }: { slug: string; block: MapBlock }) {
  const data = useWidgetData(slug, block.id);
  const geometry = useWidgetGeometry(slug, block.id);

  // Only the data query is fatal for the whole block. §6 promises the table
  // view still works when the geometry file is missing, and an error card
  // here would take the accessible path down at exactly the moment the visual
  // one has failed — which inverts the reason the table twin exists.
  if (data.error) return <WidgetError title={block.title} error={data.error} />;

  // Both queries have to have settled before there is anything coherent to
  // hand ChartCard: a `form="map"` with no `geojson` reads as "could not be
  // drawn" (ChartCard.tsx), which is the wrong kind of empty for geometry
  // that is merely still in flight.
  if (!data.data || (!geometry.data && !geometry.error)) {
    return <Skeleton height={block.height} />;
  }

  const keys = data.data.keys;
  const statuses = data.data.statuses ?? [];
  const parts: (string | null)[] = [];

  if (geometry.error) {
    parts.push(
      geometry.error instanceof Error
        ? geometry.error.message
        : "The map could not be loaded",
    );
  } else if (geometry.data) {
    const drawn = new Set(
      geometry.data.features
        .filter((f) => f.properties.role === "cluster")
        .map((f) => f.properties.name),
    );
    // A Set, not `keys.includes(...)` per drawn name below: `includes` inside
    // a loop is quadratic, which is a million-plus comparisons at the
    // 1000-cluster ceiling this widget supports, on every render.
    const keySet = new Set(keys);

    // A cluster missing its boundary, or a boundary missing its status, is
    // expected mid-study (surveys and shapefiles land on different schedules)
    // and belongs in a footnote, not an error.
    const noBoundary = keys.filter((k) => !drawn.has(k)).length;
    const noStatus = [...drawn].filter((n) => !keySet.has(n)).length;
    parts.push(noBoundary > 0 ? `${noBoundary} cluster(s) have no boundary` : null);
    parts.push(noStatus > 0 ? `${noStatus} boundary/boundaries have no status` : null);
  }

  // A status the data carries that dashboard.yaml never declared (§6). The
  // gray fill and the "Unrecognised" tooltip are the passive half of that
  // promise; without a count, a typo in 1 of 300 rows is findable only by
  // hovering 300 polygons. Null is excluded on purpose: null is "no status",
  // a different thing from a misspelt one, and `statusLabel` already keeps
  // the two apart — counting it here would contradict that.
  const declared = new Set(block.statuses.map((s) => s.value));
  const unrecognised = statuses.filter((s) => s != null && !declared.has(s)).length;
  parts.push(
    unrecognised > 0 ? `${unrecognised} cluster(s) have an unrecognised status` : null,
  );

  const footnote = parts.filter(Boolean).join("; ");

  return (
    <ChartCard
      title={block.title}
      form="map"
      keys={keys}
      values={data.data.values}
      statuses={statuses}
      statusStyles={block.statuses}
      geojson={geometry.data}
      mapHeight={block.height}
      mapLabels={block.labels}
      keyLabel="Cluster"
      valueLabel="Households"
      isRefreshing={data.isFetching || geometry.isFetching}
      footnote={footnote ? `${footnote}.` : undefined}
    />
  );
}

function TableBlock({ slug, widget }: { slug: string; widget: TableWidget }) {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string | null>>({});
  const [search, setSearch] = useState("");
  const scheme = useComputedColorScheme("light");

  // Typing is not a query per keystroke. The box stays instant; the request
  // waits for a pause.
  const [debouncedSearch] = useDebouncedValue(search, 300);

  // The rows the controls select. The page and the download share it, so the
  // file holds exactly what the table shows, just not one page of it.
  const selection = new URLSearchParams();
  for (const [column, value] of Object.entries(filters)) {
    if (value) selection.append("filter", `${column}:${value}`);
  }
  if (debouncedSearch) selection.set("search", debouncedSearch);

  const params = new URLSearchParams(selection);
  params.set("page", String(page));

  const download = useMutation({
    mutationFn: () =>
      api.download(`/api/projects/${slug}/table/${widget.id}/xlsx?${selection}`),
    onError: (err) =>
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not download the file.",
      }),
  });

  const query = useQuery<TablePage>({
    queryKey: ["table", slug, widget.id, page, filters, debouncedSearch],
    queryFn: () =>
      api.get<TablePage>(`/api/projects/${slug}/table/${widget.id}?${params}`),
    placeholderData: (previous) => previous,
  });

  // A narrowed table has fewer pages; staying on page 9 would show nothing.
  useEffect(() => setPage(1), [debouncedSearch]);

  if (query.error) return <WidgetError title={widget.title} error={query.error} />;

  const data = query.data;
  const pages = data ? Math.ceil(data.total_rows / data.page_size) : 1;

  function choose(column: string, value: string | null) {
    setPage(1);
    setFilters((current) => ({ ...current, [column]: value }));
  }

  return (
    <Stack gap="sm">
      {/* Filters sit in one row above everything they scope, never inside the
          card they filter. */}
      {(widget.filters.length > 0 || widget.search.length > 0 || widget.download) && (
        <Group align="flex-end">
          {widget.search.length > 0 && (
            <TextInput
              label={`Search ${widget.search.join(", ")}`}
              size="sm"
              w={260}
              value={search}
              placeholder="Any part of the value"
              leftSection={<IconSearch size={15} />}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
          )}

          {widget.filters.map((column) => (
            <Select
              key={column}
              label={column}
              size="sm"
              w={220}
              clearable
              placeholder="All"
              data={data?.filter_options[column] ?? []}
              value={filters[column] ?? null}
              onChange={(value) => choose(column, value)}
            />
          ))}

          {/* Last in the row: it acts on everything to its left. */}
          {widget.download && (
            // Mantine's light variant in Excel green: the file type reads at a
            // glance without outweighing the table. Excel's own #107C41 clears
            // 4.5:1 on the light tint but not on the dark one (2.6:1), so dark
            // mode takes a brighter shade of the same green.
            <Button
              variant="light"
              color={scheme === "dark" ? "#5CB885" : "#107C41"}
              size="sm"
              leftSection={<IconDownload size={15} />}
              loading={download.isPending}
              onClick={() => download.mutate()}
            >
              Download XLSX
            </Button>
          )}
        </Group>
      )}

      <Card withBorder padding="lg">
        <Group justify="space-between" align="baseline" mb="md">
          <Text fw={600}>{widget.title}</Text>
          <Text size="sm" c="dimmed">
            {(data?.total_rows ?? 0).toLocaleString()} rows
          </Text>
        </Group>

        <div
          style={{
            opacity: query.isFetching ? 0.55 : 1,
            transition: "opacity 150ms ease",
          }}
        >
          <DataTable columns={data?.columns ?? []} rows={data?.rows ?? []} />
        </div>

        {pages > 1 && (
          <Group justify="center" mt="md">
            <Pagination total={pages} value={page} onChange={setPage} size="sm" />
          </Group>
        )}
      </Card>
    </Stack>
  );
}

function WidgetError({ title, error }: { title: string; error: unknown }) {
  return (
    <Alert color="orange" title={`Could not load "${title}"`} h="100%">
      {error instanceof Error ? error.message : "Unexpected error."}
    </Alert>
  );
}

/** "Count", or "Mean distance_km" — names the measure for axes and tooltips. */
function measureLabel(agg: string, column: string | null): string {
  const name = agg.charAt(0).toUpperCase() + agg.slice(1);
  return column ? `${name} ${column}` : name;
}
